import { useCallback, useEffect, useState } from 'react';
import type { Cohort, RequirementPlan, RequirementTemplate } from '@starter/shared';
import { useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';

type FormMode = 'create' | 'assign' | null;

export function RequirementsPage() {
  const programId = useProgramId();
  const [plans, setPlans] = useState<RequirementPlan[]>([]);
  const [templates, setTemplates] = useState<RequirementTemplate[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<FormMode>(null);
  const [assigningPlan, setAssigningPlan] = useState<RequirementPlan | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [assignCohortId, setAssignCohortId] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.listRequirementPlans({ programId, page: 1, pageSize: 100 }),
      api.listRequirementTemplates(programId),
      api.listCohorts({ programId, page: 1, pageSize: 100 }),
    ])
      .then(([planRes, templateRes, cohortRes]) => {
        setPlans(planRes.data);
        setTemplates(templateRes.data);
        setCohorts(cohortRes.data);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load requirement plans'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  function openCreate() {
    setName('');
    setDescription('');
    setTemplateId('');
    setFormError(null);
    setMode('create');
  }

  function openAssign(plan: RequirementPlan) {
    setAssigningPlan(plan);
    setAssignCohortId('');
    setFormError(null);
    setMode('assign');
  }

  function closeModal() {
    setMode(null);
    setAssigningPlan(null);
    setFormError(null);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await api.createRequirementPlan({
        programId,
        name,
        description: description || null,
        templateId: templateId || null,
      });
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!assigningPlan) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.assignRequirementPlan(assigningPlan.id, assignCohortId);
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Assign failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleActivate(planId: string) {
    setActionLoading(planId);
    setError(null);
    try {
      await api.activateRequirementPlan(planId);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Activation failed');
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Requirement Plans"
        subtitle="Graduation case requirements by cohort"
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Create plan
          </button>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading requirement plans…" />
      ) : (
        <div className="card">
          {plans.length === 0 ? (
            <p className="text-muted">No requirement plans yet. Create one from scratch or a template.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Active version</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{p.description ?? '—'}</td>
                      <td>{p.currentVersionId ? 'Yes' : 'Draft only'}</td>
                      <td className="table-actions">
                        {!p.currentVersionId && (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            disabled={actionLoading === p.id}
                            onClick={() => void handleActivate(p.id)}
                          >
                            {actionLoading === p.id ? 'Activating…' : 'Activate'}
                          </button>
                        )}
                        {p.currentVersionId && (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => openAssign(p)}
                          >
                            Assign cohort
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={mode === 'create'}
        title="Create requirement plan"
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="plan-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        }
      >
        <form id="plan-form" onSubmit={handleCreate}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Plan name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </label>
          <label className="field">
            <span className="field__label">Start from template (optional)</span>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Blank plan</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </form>
      </Modal>

      <Modal
        open={mode === 'assign'}
        title={`Assign ${assigningPlan?.name ?? 'plan'} to cohort`}
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="assign-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        }
      >
        <form id="assign-form" onSubmit={handleAssign}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Cohort</span>
            <select value={assignCohortId} onChange={(e) => setAssignCohortId(e.target.value)} required>
              <option value="">Select cohort…</option>
              {cohorts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </label>
        </form>
      </Modal>
    </div>
  );
}
